/**
 * ShareContentBubble — per-type subcomponent for `ChatMessageType.sharePhoto`
 * AND `ChatMessageType.shareVideo`. One component parameterised by `mediaType`
 * (0 = photo, 1 = video); the two variants are structurally identical except
 * for the centered play-icon overlay on the video.
 *
 * Flutter source of truth:
 *  - Own variant: `currentUserMessageTypeShareContent` (`chat_screen.dart:2087-2282`).
 *  - Peer variant: `peerUserMessageTypeShareContent` (`chat_screen.dart:3214-3410`).
 *
 * Both Flutter variants share the same shape:
 *  - Outer column with `crossAxisAlignment.end` (own) / `.start` (peer).
 *  - Inner row containing the share-icon-square (40×40 round) and the media
 *    thumbnail bubble; the ownership-driven row order lives in
 *    `ShareBubbleRow`.
 *  - Optional caption bubble below the row when `chatMessage.content` is
 *    non-empty. Background is `AppColors.ownMessageColor` on BOTH variants —
 *    same convention as `ShareUserBubble`'s optional content bubble; the two
 *    share one owner, `ShareCaptionBubble`, which carries the Flutter anchors.
 *
 * Co-located subcomponents (each carries its own Flutter anchors and
 * theme/responsive mappings); all but the media thumb are shared with
 * `ShareUserBubble`:
 *  - `ShareBubbleRow`         — the icon + payload row, incl. the row order.
 *  - `ShareContentMediaThumb` — the `MediaBubbleFrame` + thumbnail + play
 *                               overlay.
 *  - `ShareCaptionBubble`     — the optional caption.
 *
 * Differences from `ImageBubble` / `VideoBubble`: the share-icon-square beside
 * the media, the optional caption bubble below it, and a thumbnail resolved
 * through the content CDN rather than the chat bucket (see
 * `ShareContentMediaThumb`).
 *
 * Tap behaviour: opens the Content Details page for the single shared media,
 * resolved by the media's relative `path` — Flutter navigates a share-content
 * bubble to `ContentListScreen` with `fetchSingleMediaPath:
 * shareInfo!.mediaInfo!.relativePath` (`chat_screen.dart:2132` own / `:3261`
 * peer), which resolves the media via `MediasRepository.getMediaByPath`
 * (`content_list_screen.dart:144-147`). The web mirrors this with
 * `Navigator.navigateToContentDetailsByPath(shareInfo.mediaInfo.path)` (the
 * same `MediaInfoEntity.path`-by-path navigation `CommentMediaThumbnail` /
 * `ContentListItem` use), reusing the `getUserMediaByPathUseCase` chain.
 * Path — NOT share-link: Flutter's chat share flow passes
 * `fetchSingleMediaPath`, and the only `fetchSingleMediaShareLink` caller in
 * the Flutter app is the external deep-link handler
 * (`app_links_service.dart:110`), a different flow — so the web opens by path.
 *
 * Behaviour when `shareInfo` is null (defensive): Flutter dereferences
 * `shareInfo!.mediaInfo!` repeatedly inside both variants — a `null`
 * `shareInfo` on a real share-content message would crash. The web port
 * mirrors the strict expectation but never crashes: if `shareInfo` or
 * `shareInfo.mediaInfo` is missing, `ShareContentMediaThumb` renders the frame
 * with an empty placeholder (no URL, no aspect ratio override) and the tap is
 * a no-op (no `path` to navigate to). This is a small defensive deviation —
 * easier to debug than a runtime crash.
 *
 * Container element: rendered as `<div role="button" tabIndex={0}>` with
 * `useEnterOrSpaceActivation` for keyboard parity — see `TextBubble.tsx`
 * for the rationale.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { ChatUtils } from '@/domain/utils/chatUtils';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ShareBubbleRow } from './ShareBubbleRow';
import { ShareCaptionBubble } from './ShareCaptionBubble';
import {
  ShareContentMediaThumb,
  type ShareContentMediaType,
} from './ShareContentMediaThumb';

export type { ShareContentMediaType };

interface ShareContentBubbleProps extends BubbleSubcomponentProps {
  /**
   * Photo or video. Drives the centered play-overlay (rendered only when
   * `mediaType === 1`). Sourced from the dispatcher registration — NOT
   * from `shareInfo.mediaInfo.type` — so the two ChatMessageType
   * registrations stay distinct even when `shareInfo` happens to be
   * malformed.
   */
  mediaType: ShareContentMediaType;
}

const ShareContentBubble: React.FC<ShareContentBubbleProps> = ({
  message,
  isOwn,
  isLastOfRun,
  mediaType,
}) => {
  const responsive = useResponsiveDesign();

  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // `share-photo` / `share-video` token for the bubble qa id. Threaded into
  // `ShareContentMediaThumb` so the media handle can never drift from this one.
  const typeToken = mediaType === 1 ? 'share-video' : 'share-photo';

  // Tap → open the Content Details page for this single shared media, resolved
  // by its relative `path`. Flutter (`chat_screen.dart:2127-2134` own /
  // `:3256-3262` peer) calls `NavigationService.navigateToContentListScreen(...
  // fetchSingleMediaPath: shareInfo!.mediaInfo!.relativePath)`; the web mirrors
  // it via the same-purpose `navigateToContentDetailsByPath` (reusing the
  // `getUserMediaByPathUseCase` chain), matching `CommentMediaThumbnail` /
  // `ContentListItem`. `MediaInfoEntity.path` == Flutter `MediaInfo.relativePath`
  // (both are the Firestore `path` field).
  const handleTap = (): void => {
    const path = message.shareInfo?.mediaInfo?.path;
    // Flutter guards `shareInfo == null` (`chat_screen.dart:2124`) then
    // null-asserts `mediaInfo!.relativePath`. The web guards the whole
    // `shareInfo?.mediaInfo?.path` chain — a strict superset that never
    // crashes and never navigates with an empty path.
    if (!path) return;
    // Deviations from Flutter, both intentional:
    //  - Flutter clears composer focus with `_focusNode.unfocus()`
    //    (`:2135`/`:3264`) — a no-op on web: the React-Router navigation to
    //    `/content/@…` unmounts/hides the chat composer, so there is no focus
    //    to clear.
    //  - Flutter passes an optimistic `medias: [singleMedia]` for its hero
    //    transition; the web has no hero transition and follows the
    //    established navigate-by-path pattern, so Content Details briefly shows
    //    its own loading indicator, then the fetched media. No optimistic media.
    Navigator.navigateToContentDetailsByPath(path);
  };

  const handleKeyDown = useEnterOrSpaceActivation<HTMLDivElement>(handleTap);

  return (
    <div
      {...qaAttr({ id: `chat-bubble-${typeToken}-${message.documentId}` })}
      role="button"
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={handleKeyDown}
      style={{
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        // Flutter's outer column uses `crossAxisAlignment.end` for own
        // (`chat_screen.dart:2137`) / `.start` for peer (`:3265`).
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      <ShareBubbleRow isOwn={isOwn}>
        <ShareContentMediaThumb
          message={message}
          isOwn={isOwn}
          isLastOfRun={isLastOfRun}
          mediaType={mediaType}
          qaTypeToken={typeToken}
          onTap={handleTap}
        />
      </ShareBubbleRow>
      <ShareCaptionBubble message={message} corners={corners} isOwn={isOwn} />
    </div>
  );
};

// Wrappers that bind the `mediaType` prop for each registered
// `ChatMessageType`. Keeps the dispatcher contract (`BubbleSubcomponentProps`
// only) intact — the dispatcher does not know about `mediaType`; it just
// hands a different component for each registered type.
const SharePhotoBubble: React.FC<BubbleSubcomponentProps> = (props) => (
  <ShareContentBubble {...props} mediaType={0} />
);
const ShareVideoBubble: React.FC<BubbleSubcomponentProps> = (props) => (
  <ShareContentBubble {...props} mediaType={1} />
);

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.sharePhoto` and `ChatMessageType.shareVideo` to this
// component (with the matching `mediaType` prop). See `MessageBubble.tsx`
// for the registry contract.
registerBubble(ChatMessageType.sharePhoto, SharePhotoBubble);
registerBubble(ChatMessageType.shareVideo, ShareVideoBubble);

export { ShareContentBubble };
